import { ScreenHelp } from "@/components/Help";
import {
  Card,
  Pill,
  SectionTitle,
  StatusBadge,
  tdBase,
  thBase,
  totalCellBase,
} from "@/components/ui";
import { type PartPlan, multiYearPnL } from "@/lib/cost/projection";
import {
  type QuoteMeta,
  type ReportPart,
  customerQuote,
  internalCostReport,
} from "@/lib/cost/report";
import { type StatusKey, fmt, fmtKRW, isoDate, pct } from "@/lib/data";
import { useIsMobile } from "@/lib/useViewport";
import { type CSSProperties, useState } from "react";

// CostReport.tsx — 내부 원가보고서 + 고객 견적서 출력 (이슈 #12).
//
// 두 모드: (1) 내부용 — 원가 내역 전부 노출(재료/가공/관리/금형/내부원가/영업이익/이익율),
//          (2) 고객용 — 단가(견적가)/공급가만, 원가 비노출.
// 집계/shape 로직은 전부 `@/lib/cost/report` 에 있고 이 컴포넌트는 입력(ReportPart)을
// 받아 렌더만 한다(계산 금지). 출력은 window.print() + @media print CSS.
// 다년 손익 첨부 옵션은 `@/lib/cost/projection` multiYearPnL 사용.
// recharts/react-router/react-query 미사용 — plain table (레포 컨벤션).

// 보고서 입력 — 아직 백엔드/입력 화면 연동이 없어 항상 null.
// 연동 시 이 함수를 hooks/api 호출로 교체한다. null 이면 빈 상태 메시지를 표시한다.
interface ReportData {
  parts: ReportPart[];
  quoteMeta: Omit<QuoteMeta, "issueDate">;
  projection: { parts: PartPlan[]; years: number[] };
}
function loadReportData(): ReportData | null {
  return null;
}

// 보고서 표는 컬럼이 많아(내부용 20컬럼) 공용 베이스보다 한 단계 더 촘촘하게.
const thStyle: CSSProperties = {
  ...thBase,
  padding: "9px 12px",
  fontSize: 11,
  letterSpacing: "0.4px",
};
const tdStyle: CSSProperties = { ...tdBase, padding: "8px 12px", fontSize: 12.5 };
const tdLabel: CSSProperties = {
  ...tdStyle,
  textAlign: "left",
  color: "var(--text-base)",
  fontWeight: 700,
};
const totalCell: CSSProperties = { ...totalCellBase, padding: "8px 12px", fontSize: 12.5 };

const num = (n: number): string => fmt(n);
const dec = (n: number): string => n.toLocaleString("ko-KR", { maximumFractionDigits: 3 });

// ---- 내부 원가보고서 (모든 원가 컬럼) ----
const InternalReportView = ({
  data,
  attachProjection,
}: {
  data: ReportData;
  attachProjection: boolean;
}) => {
  const mobile = useIsMobile();
  const report = internalCostReport(data.parts);
  const proj = attachProjection
    ? multiYearPnL(data.projection.parts, { years: data.projection.years })
    : null;
  const projYears = data.projection.years;

  return (
    <Card pad={0}>
      <div style={{ padding: "18px 24px 4px" }}>
        <SectionTitle sub="내부원가보고!A8:Y25 — 원가 내역 전부 노출 (재료/가공/관리/금형/내부원가/영업이익/이익율)">
          내부 원가보고서
        </SectionTitle>
      </div>
      <div style={{ overflowX: "auto" }}>
        {/* 20컬럼 — 좁은 폭에서 뭉개지지 않도록 최소폭을 줘 가로 스크롤되게 한다. */}
        <table
          style={{ width: "100%", minWidth: mobile ? 1400 : undefined, borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left" }}>품번</th>
              <th style={{ ...thStyle, textAlign: "left" }}>품명</th>
              <th style={{ ...thStyle, textAlign: "left" }}>공법</th>
              <th style={thStyle}>년간소요량</th>
              <th style={{ ...thStyle, textAlign: "left" }}>재질</th>
              <th style={thStyle}>단중</th>
              <th style={thStyle}>재료비</th>
              <th style={thStyle}>표면처리</th>
              <th style={thStyle}>가공</th>
              <th style={thStyle}>검사·포장</th>
              <th style={thStyle}>제조원가</th>
              <th style={thStyle}>관리비</th>
              <th style={thStyle}>재료관리비</th>
              <th style={thStyle}>금형비</th>
              <th style={{ ...thStyle, color: "var(--text-base)" }}>내부원가</th>
              <th style={{ ...thStyle, color: "var(--text-base)" }}>견적가</th>
              <th style={{ ...thStyle, color: "var(--num-positive)" }}>영업이익</th>
              <th style={{ ...thStyle, color: "var(--num-positive)" }}>이익율</th>
              <th style={thStyle}>도금</th>
              <th style={thStyle}>열처리</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((r) => (
              <tr key={r.partNo}>
                <td style={tdLabel}>{r.partNo}</td>
                <td style={{ ...tdStyle, textAlign: "left" }}>{r.name}</td>
                <td style={{ ...tdStyle, textAlign: "left" }}>{r.method}</td>
                <td style={tdStyle}>{num(r.annualVolume)}</td>
                <td style={{ ...tdStyle, textAlign: "left" }}>{r.material}</td>
                <td style={tdStyle}>{dec(r.netWeight)}</td>
                <td style={tdStyle}>{dec(r.materialCost)}</td>
                <td style={tdStyle}>{dec(r.surfaceCost)}</td>
                <td style={tdStyle}>{dec(r.processing)}</td>
                <td style={tdStyle}>{dec(r.inspectionPacking)}</td>
                <td style={tdStyle}>{dec(r.manufacturingCost)}</td>
                <td style={tdStyle}>{dec(r.generalAdmin)}</td>
                <td style={tdStyle}>{dec(r.materialAdmin)}</td>
                <td style={tdStyle}>{dec(r.mold)}</td>
                <td style={{ ...tdStyle, color: "var(--text-base)", fontWeight: 700 }}>
                  {dec(r.internalCost)}
                </td>
                <td style={{ ...tdStyle, color: "var(--text-base)", fontWeight: 700 }}>
                  {dec(r.supplyPrice)}
                </td>
                <td style={{ ...tdStyle, color: "var(--num-positive)" }}>
                  {dec(r.operatingProfit)}
                </td>
                <td style={{ ...tdStyle, color: "var(--num-positive)", fontWeight: 700 }}>
                  {pct(r.profitRate * 100)}
                </td>
                <td style={tdStyle}>{dec(r.plating)}</td>
                <td style={tdStyle}>{dec(r.heatTreat)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...totalCell, textAlign: "left" }}>합계</td>
              <td style={totalCell} />
              <td style={totalCell} />
              <td style={totalCell}>{num(report.totals.annualVolume)}</td>
              <td style={totalCell} />
              <td style={totalCell} />
              <td style={totalCell}>{dec(report.totals.materialCost)}</td>
              <td style={totalCell}>{dec(report.totals.surfaceCost)}</td>
              <td style={totalCell}>{dec(report.totals.processing)}</td>
              <td style={totalCell}>{dec(report.totals.inspectionPacking)}</td>
              <td style={totalCell}>{dec(report.totals.manufacturingCost)}</td>
              <td style={totalCell}>{dec(report.totals.generalAdmin)}</td>
              <td style={totalCell}>{dec(report.totals.materialAdmin)}</td>
              <td style={totalCell}>{dec(report.totals.mold)}</td>
              <td style={totalCell}>{dec(report.totals.internalCost)}</td>
              <td style={totalCell}>{dec(report.totals.supplyPrice)}</td>
              <td style={{ ...totalCell, color: "var(--num-positive)" }}>
                {dec(report.totals.operatingProfit)}
              </td>
              <td style={{ ...totalCell, color: "var(--num-positive)" }}>
                {pct(report.totals.profitRate * 100)}
              </td>
              <td style={totalCell}>{dec(report.totals.plating)}</td>
              <td style={totalCell}>{dec(report.totals.heatTreat)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {proj && (
        <div style={{ padding: "8px 24px 24px" }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--text-muted)",
              margin: "16px 0 10px",
            }}
          >
            다년 손익 첨부 ({projYears[0]}–{projYears[projYears.length - 1]}) — 내부원가(SOP) 고정,
            견적가 CR 후 동결
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: "left" }}>부품</th>
                <th style={thStyle}>총 매출</th>
                <th style={thStyle}>총 손익</th>
                <th style={{ ...thStyle, color: "var(--num-positive)" }}>총 손익율</th>
              </tr>
            </thead>
            <tbody>
              {proj.parts.map((p) => (
                <tr key={p.name}>
                  <td style={tdLabel}>{p.name}</td>
                  <td style={tdStyle}>{fmtKRW(p.totalRevenue)}</td>
                  <td style={tdStyle}>{fmtKRW(p.totalProfit)}</td>
                  <td style={{ ...tdStyle, color: "var(--num-positive)" }}>
                    {pct(p.totalProfitRate * 100)}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ ...totalCell, textAlign: "left" }}>계</td>
                <td style={totalCell}>{fmtKRW(proj.totalRevenue)}</td>
                <td style={totalCell}>{fmtKRW(proj.totalProfit)}</td>
                <td style={{ ...totalCell, color: "var(--num-positive)" }}>
                  {pct(proj.totalProfitRate * 100)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

// ---- 고객 견적서 (단가/공급가만, 원가 비노출) ----
const headerRow: CSSProperties = { display: "flex", gap: 12, fontSize: 13.5, padding: "5px 0" };
const headerKey: CSSProperties = {
  width: 90,
  color: "var(--text-muted)",
  fontWeight: 700,
  flexShrink: 0,
};

const CustomerQuoteView = ({ data }: { data: ReportData }) => {
  const quote = customerQuote(data.parts, { ...data.quoteMeta, issueDate: isoDate(new Date()) });

  return (
    <Card pad={0}>
      <div style={{ padding: "18px 24px 4px" }}>
        <SectionTitle
          sub="견적서 — 단가(견적가)/공급가만 노출. 원가 내역 비노출."
          right={<StatusBadge status={quote.status as StatusKey} />}
        >
          고객 제출용 견적서
        </SectionTitle>
      </div>
      <div style={{ padding: "0 24px 16px", maxWidth: 560 }}>
        <div style={headerRow}>
          <span style={headerKey}>문서번호</span>
          <span style={{ color: "var(--text-base)", fontWeight: 700 }}>{quote.docNo}</span>
        </div>
        <div style={headerRow}>
          <span style={headerKey}>시행일자</span>
          <span style={{ color: "var(--text-strong)" }}>{quote.issueDate}</span>
        </div>
        <div style={headerRow}>
          <span style={headerKey}>수신</span>
          <span style={{ color: "var(--text-strong)" }}>{quote.recipient}</span>
        </div>
        <div style={headerRow}>
          <span style={headerKey}>참조</span>
          <span style={{ color: "var(--text-strong)" }}>{quote.reference}</span>
        </div>
        <div style={headerRow}>
          <span style={headerKey}>제목</span>
          <span style={{ color: "var(--text-base)", fontWeight: 700 }}>{quote.title}</span>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left" }}>품번</th>
              <th style={{ ...thStyle, textAlign: "left" }}>품목</th>
              <th style={thStyle}>수량</th>
              <th style={thStyle}>단가</th>
              <th style={{ ...thStyle, color: "var(--num-positive)" }}>공급가</th>
            </tr>
          </thead>
          <tbody>
            {quote.lines.map((l) => (
              <tr key={l.partNo}>
                <td style={tdLabel}>{l.partNo}</td>
                <td style={{ ...tdStyle, textAlign: "left" }}>{l.name}</td>
                <td style={tdStyle}>{num(l.quantity)}</td>
                <td style={tdStyle}>{dec(l.unitPrice)}</td>
                <td style={{ ...tdStyle, color: "var(--num-positive)" }}>
                  {fmtKRW(l.supplyAmount)}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ ...totalCell, textAlign: "left" }}>공급가 합계</td>
              <td style={totalCell} />
              <td style={totalCell} />
              <td style={totalCell} />
              <td style={{ ...totalCell, color: "var(--num-positive)" }}>
                {fmtKRW(quote.totalSupplyAmount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

type Mode = "internal" | "customer";

const COST_REPORT_HELP = [
  "부품 원가 빌드업 결과를 보고서 형태로 집계해 출력합니다 — 화면에서 재계산하지 않습니다.",
  "'내부용' 모드는 재료/가공/관리/금형/내부원가/영업이익/이익율 등 원가 내역을 전부 노출합니다.",
  "'고객용' 모드는 단가(견적가)와 공급가만 표시하고 원가는 노출하지 않습니다.",
  "내부용에서 '다년 손익 첨부' 를 체크하면 다년 매출 · 손익 · 손익율 표가 함께 출력됩니다.",
  "'인쇄 / PDF' 버튼은 브라우저 인쇄 대화상자를 엽니다 — 도움말 · 버튼 등 비출력 요소는 자동 제외됩니다.",
];

export const CostReport = ({ onToast }: { onToast: (msg: string) => void }) => {
  const mobile = useIsMobile();
  const [mode, setMode] = useState<Mode>("internal");
  const [attachProjection, setAttachProjection] = useState(false);

  const data = loadReportData();

  const onPrint = () => {
    onToast(
      `${mode === "internal" ? "내부 원가보고서" : "고객 견적서"} 인쇄 — 출력 대화상자를 엽니다.`
    );
    window.print();
  };

  if (!data || data.parts.length === 0) {
    return (
      <div style={{ padding: mobile ? 14 : 28, display: "flex", flexDirection: "column", gap: 24 }}>
        <SectionTitle sub="부품 원가 빌드업 결과를 집계·출력 — 내부용(원가 전부)/고객용(단가만) 분리. 재계산 없음.">
          원가 출력
        </SectionTitle>
        <ScreenHelp items={COST_REPORT_HELP} />
        <Card>
          <div
            style={{
              padding: "48px 0",
              textAlign: "center",
              color: "var(--text-faint)",
              fontSize: 14,
            }}
          >
            표시할 내용이 없습니다 — 등록된 부품 원가 데이터가 없습니다.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: mobile ? 14 : 28, display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionTitle
        sub="부품 원가 빌드업 결과를 집계·출력 — 내부용(원가 전부)/고객용(단가만) 분리. 재계산 없음."
        right={
          <div
            className="cr-no-print"
            style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}
          >
            <Pill
              variant={mode === "internal" ? "primary" : "ghost"}
              size="md"
              onClick={() => setMode("internal")}
            >
              내부용
            </Pill>
            <Pill
              variant={mode === "customer" ? "primary" : "ghost"}
              size="md"
              onClick={() => setMode("customer")}
            >
              고객용
            </Pill>
            <Pill variant="dark" size="md" onClick={onPrint}>
              인쇄 / PDF
            </Pill>
          </div>
        }
      >
        원가 출력
      </SectionTitle>

      <div className="cr-no-print">
        <ScreenHelp items={COST_REPORT_HELP} />
      </div>

      {mode === "internal" && (
        <label
          className="cr-no-print"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13.5,
            color: "var(--text-strong)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={attachProjection}
            onChange={(e) => setAttachProjection(e.target.checked)}
          />
          다년 손익 첨부 (매출·손익·손익율)
        </label>
      )}

      {mode === "internal" ? (
        <InternalReportView data={data} attachProjection={attachProjection} />
      ) : (
        <CustomerQuoteView data={data} />
      )}

      <style>{"@media print{.cr-no-print{display:none!important}body{background:#ffffff}}"}</style>
    </div>
  );
};
